/**
 * Bloco operacional da tela gerencial `/frota/manutencao`.
 *
 * O gestor vê aqui o que a operação registrou — checklists, ocorrências, O.S. e
 * veículos com pendência — e abre o Portal Operacional no celular. Os números
 * vêm do MESMO serviço que alimenta o início do portal: um só significado para
 * "ocorrência aberta".
 *
 * Abaixo deste bloco a tela continua sendo o BI de CUSTO do ERP, que é outra
 * grandeza e não se mistura com esta.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { MaintenanceManagementOverviewDto } from "@bi/types";
import { api } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { KPIStatCard } from "../../components/KPIStatCard";
import { EmptyState } from "../../components/EmptyState";
import { IconClipboard, IconExternalLink } from "../../components/icons";
import { KIND_LABEL, RUN_STATUS } from "./labels";
import { openPortal } from "../portal/open-app";
import { portalPaths } from "../portal/portal-nav";

export function AbrirAppButton({ className = "btn-primary" }: { className?: string }) {
  return (
    <button className={className} onClick={() => openPortal()} title="Abre o Portal Operacional, otimizado para celular">
      <IconExternalLink className="w-4 h-4" />
      Abrir App
    </button>
  );
}

export function MaintenanceOperationsPanel({ from, to }: { from: string; to: string }) {
  const { data: o, isLoading } = useQuery({
    queryKey: ["maintenance", "overview", from, to],
    queryFn: () => api.get<MaintenanceManagementOverviewDto>(`/maintenance/overview?from=${from}&to=${to}`),
    staleTime: 30_000,
  });

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Operação: checklists e ocorrências</h3>
          <p className="text-sm text-gray-500">O que o pátio registrou no SIAG. Abaixo, o custo de manutenção vindo do ERP.</p>
        </div>
        {/* Atalhos: no celular rolam para o lado em vez de quebrar o texto dos botões */}
        <div className="-mx-4 flex max-w-[100vw] items-center gap-2 overflow-x-auto px-4 sm:mx-0 sm:px-0 [&>*]:shrink-0 [&>*]:whitespace-nowrap">
          <Link className="btn-secondary" to="/frota/manutencao/checklists">Checklists</Link>
          <Link className="btn-secondary" to="/frota/manutencao/nao-conformidades">Não conformidades</Link>
          <Link className="btn-secondary" to="/frota/manutencao/ordens">Ordens de serviço</Link>
          <Link className="btn-secondary" to="/frota/manutencao/preventiva">Plano preventivo</Link>
          <Link className="btn-primary" to="/frota/manutencao">Central de manutenção</Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPIStatCard label="Checklists em andamento" memory={{ cardKey: "manutencao.checklists-pendentes", from, to, expected: o?.runsInProgress }} value={o ? fmtNumber(o.runsInProgress) : "—"}
          subtitle={o ? `${fmtNumber(o.runsCompletedInPeriod)} concluídos no período` : undefined}
          loading={isLoading} status={o && o.runsInProgress > 0 ? "warning" : "neutral"} />
        <KPIStatCard label="Não conformidades abertas" memory={{ cardKey: "manutencao.nc-abertas", from, to, expected: o?.nonConformitiesOpen }} value={o ? fmtNumber(o.nonConformitiesOpen) : "—"}
          subtitle={o ? `${fmtNumber(o.nonConformitiesCritical)} críticas` : undefined}
          loading={isLoading} status={o ? (o.nonConformitiesCritical > 0 ? "critical" : o.nonConformitiesOpen > 0 ? "warning" : "good") : "neutral"} />
        <KPIStatCard label="Críticas sem O.S." memory={{ cardKey: "manutencao.criticas-sem-os", expected: o?.criticalWithoutWorkOrder }} value={o ? fmtNumber(o.criticalWithoutWorkOrder) : "—"}
          subtitle="ocorrência grave sem ordem aberta"
          loading={isLoading} status={o && o.criticalWithoutWorkOrder > 0 ? "critical" : "good"} />
        <KPIStatCard label="O.S. abertas" memory={{ cardKey: "manutencao.os-ativas", expected: o?.workOrdersOpen }} value={o ? fmtNumber(o.workOrdersOpen) : "—"}
          subtitle={o ? `${fmtNumber(o.workOrdersByType.preventive)} preventivas · ${fmtNumber(o.workOrdersByType.corrective)} corretivas` : undefined}
          loading={isLoading} status={o && o.workOrdersOverdue > 0 ? "critical" : "neutral"} />
        <KPIStatCard label="Pneus críticos" memory={{ cardKey: "manutencao.pneus-criticos", expected: o?.tiresCritical.available ? o.tiresCritical.value : null }} value={o?.tiresCritical.available ? fmtNumber(o.tiresCritical.value) : "—"}
          subtitle="abaixo do sulco de segurança"
          insight={o && !o.inspectionsOverdue.available ? o.inspectionsOverdue.reason : undefined}
          loading={isLoading} status={o?.tiresCritical.available && o.tiresCritical.value > 0 ? "warning" : "neutral"} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Checklists recentes */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Checklists recentes</p>
          </div>
          {(o?.recentRuns.length ?? 0) === 0 ? (
            <EmptyState compact icon={<IconClipboard className="w-5 h-5" />} title="Nenhum checklist registrado"
              description="Quando a operação usar o app, os checklists aparecem aqui." />
          ) : (
            <div className="divide-y divide-surface-border">
              {o!.recentRuns.map((r) => (
                <Link key={r.id} to={`/frota/manutencao/checklists/${r.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-surface-muted">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-900 truncate">{r.templateName ?? KIND_LABEL[r.kind]}</p>
                    <p className="text-xs text-gray-500 truncate">
                      <span className="font-mono">{r.vehiclePlate}</span>
                      {r.trailerPlate && <span className="font-mono"> + {r.trailerPlate}</span>}
                      {r.driverName ? ` · ${r.driverName}` : ""}
                      {` · ${new Date(r.startedAt).toLocaleDateString("pt-BR")}`}
                    </p>
                  </div>
                  {r.nonConformities > 0 && <span className="badge-red flex-shrink-0">{r.nonConformities}</span>}
                  <span className={`${RUN_STATUS[r.status].badge} flex-shrink-0`}>{RUN_STATUS[r.status].label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Veículos com pendência */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Veículos com pendência</p>
            <p className="text-xs text-gray-400 mt-0.5">ocorrências abertas e O.S. em aberto</p>
          </div>
          {(o?.vehiclesWithPending.length ?? 0) === 0 ? (
            <EmptyState compact title="Nenhum veículo com pendência" />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-5 py-2">Placa</th>
                  <th className="text-right px-3 py-2">Ocorrências</th>
                  <th className="text-right px-3 py-2">Críticas</th>
                  <th className="text-right px-5 py-2">O.S.</th>
                </tr>
              </thead>
              <tbody>
                {o!.vehiclesWithPending.map((v) => (
                  <tr key={v.plate} className="border-t border-surface-border/60 hover:bg-surface-muted">
                    <td className="px-5 py-2">
                      <Link className="font-mono text-tm-700 hover:underline" to={`/frota/placa/${encodeURIComponent(v.plate)}`}>{v.plate}</Link>
                    </td>
                    <td className="px-3 py-2 text-right">{fmtNumber(v.openNonConformities)}</td>
                    <td className="px-3 py-2 text-right">{v.criticalNonConformities > 0 ? <span className="badge-red">{v.criticalNonConformities}</span> : "—"}</td>
                    <td className="px-5 py-2 text-right">{fmtNumber(v.openWorkOrders)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {/* Melhoria cadastral — o cadastro não diz o tipo do implemento */}
      {o && o.manualImplementClassification.runs > 0 && (
        <div className="card p-4 text-sm">
          <p className="font-medium text-gray-900">Implemento classificado à mão</p>
          <p className="text-gray-600 mt-0.5">
            {fmtNumber(o.manualImplementClassification.runs)} checklist(s) nos últimos 90 dias. O tipo do veículo não está
            preenchido no cadastro do ERP, então o operador escolhe o implemento. Corrigir o cadastro faz o SIAG escolher sozinho.
          </p>
          <p className="mt-1 font-mono text-xs text-gray-500">{o.manualImplementClassification.plates.join(" · ")}</p>
        </div>
      )}
    </section>
  );
}
